import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button, Modal, Form, InputGroup } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill, BsFileEarmarkCode } from "react-icons/bs";
import { LuFilter } from 'react-icons/lu';
import { GrAttachment } from "react-icons/gr";
import { FaTruck, FaBox, FaBoxOpen, FaRegHandPaper, FaSortUp, FaSortDown } from 'react-icons/fa';
import { PiScanFill } from "react-icons/pi";
import { IoSettingsOutline } from "react-icons/io5";
import { FiFileText } from 'react-icons/fi';
import Header from '../../../header/header';
import Navvbar from '../../../header/Navvbar';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../../../context/currencyContext';
import usePricingAccess from '../../../../hooks/usePricingAccess.js';
import './caseship.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// =====================================================
// GET USER LAB ID
// =====================================================
const getUserLabId = () => {
  try {
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];
    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          const userLabId =
            parsed.userLabId ||
            parsed.labId ||
            parsed.id ||
            (parsed.data && parsed.data.userLabId) ||
            (parsed.data && parsed.data.labId) ||
            (parsed.user && parsed.user.userLabId) ||
            (parsed.user && parsed.user.labId);
          if (userLabId) return userLabId;
          if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
            return storedData;
          }
        } catch (e) {
          continue;
        }
      }
    }
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      if (value && value.match(/^000000\d{2}$/)) {
        return value;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Short display date like "1 Oct 2026"
const formatShortDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// =====================================================
// COLUMN SORT HELPERS (same as Cases Opened)
// =====================================================
const isCaseOnHold = (c) => /hold/i.test(String(c.status || c.caseStatus || ''));

// Same status icons as Cases Opened:
//   on hold -> hand, invoiced -> closed box, in progress / open -> open box
const getStatusIcon = (c) => {
  if (isCaseOnHold(c)) {
    return { Icon: FaRegHandPaper, className: 'icon-inbox icon-hold', tip: c.status || 'On Hold' };
  }
  if (c.invoiceNumber) {
    return { Icon: FaBox, className: 'icon-inbox icon-closed-box', tip: 'Closed - Invoiced' };
  }
  return { Icon: FaBoxOpen, className: 'icon-inbox icon-open-box', tip: c.status || 'In Progress' };
};

const getSortValue = (c, key) => {
  switch (key) {
    case 'status':
      return isCaseOnHold(c) ? 2 : c.invoiceNumber ? 1 : 0;
    case 'caseNumber':
      return Number(c.caseNumber) || String(c.caseNumber || '');
    case 'invoice':
      return Number(c.invoiceNumber) || '';
    case 'account':
      return c.account_name || c.account_holder || '';
    case 'provider':
      return c.provider_name || '';
    case 'patient':
      return c.last_name || '';
    case 'accountShip':
      return c.start_date ? new Date(c.start_date).getTime() : '';
    case 'labShip':
      return c.arrive_date ? new Date(c.arrive_date).getTime() : '';
    case 'delivery':
      return c.delivery_date ? new Date(c.delivery_date).getTime() : '';
    default:
      return '';
  }
};

const sortByColumn = (list, sortConfig) => {
  if (!sortConfig.key) return list;

  const dir = sortConfig.dir === 'desc' ? -1 : 1;

  return [...list].sort((a, b) => {
    const va = getSortValue(a, sortConfig.key);
    const vb = getSortValue(b, sortConfig.key);

    if (va === '' && vb === '') return 0;
    if (va === '') return 1;
    if (vb === '') return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }

    return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
  });
};

const SortHeader = ({ label, colKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === colKey;

  return (
    <th className="table-header sortable-th" onClick={() => onSort(colKey)}>
      {label}
      <span className="sort-arrows">
        <FaSortUp className={`sort-icon${isActive && sortConfig.dir === 'asc' ? ' active' : ''}`} />
        <FaSortDown className={`sort-icon${isActive && sortConfig.dir === 'desc' ? ' active' : ''}`} />
      </span>
    </th>
  );
};

// Default Filter State
const defaultFilters = {
  by: 'To Ship',
  account: '',
  caseTag: 'Any',
  properties: 'All',
  startDate: '',
  endDate: '',
  closedCases: 'No',
  sortBy: 'Files Count',
  sortDirection: 'Descending'
};

const CaseShip = () => {
  const { formatAmount } = useCurrency();
  const { hasPricingAccess, loading: pricingLoading } = usePricingAccess();

  const [cases, setCases] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // ACCOUNTS STATE
  const [accountsList, setAccountsList] = useState([]);

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const [modalCases, setModalCases] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // FILTER STATE
  const [filters, setFilters] = useState(defaultFilters);
  const [tempFilters, setTempFilters] = useState(defaultFilters);

  // COLUMN SORT STATE (click header arrows)
  const [sortConfig, setSortConfig] = useState({ key: '', dir: 'asc' });

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  // Ref to always hold latest filters for fetchCases
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const requestIdRef = useRef(0);

  // =====================================================
  // BUILD PARAMS BASED ON FILTERS
  // =====================================================
  const buildParams = (userLabId, currentFilters) => {
    const today = new Date();
    const params = {
      userLabId,
      by: 'month',
      currentMonth: today.getMonth(),
      currentYear: today.getFullYear(),
    };

    const selectedBy = currentFilters.by || 'To Ship';

    // Map "By" dropdown to Backend Status Filters
    if (selectedBy === 'To Ship') {
      params.statusFilter = 'ship';
    } else if (selectedBy === 'On Hold') {
      params.statusFilter = 'onhold';
    } else if (selectedBy === 'Open Cases') {
      params.statusFilter = 'open';
    } else if (selectedBy !== 'Month') {
      // Pass other options as caseStatus (Backend matches it against `status` field)
      params.caseStatus = selectedBy;
    }

    // Account Filter
    if (currentFilters.account) {
      params.accountName = currentFilters.account;
    }

    // Date Range Filter
    if (currentFilters.startDate || currentFilters.endDate) {
      params.by = 'dateRange';
      if (currentFilters.startDate) params.startDate = currentFilters.startDate;
      if (currentFilters.endDate) params.endDate = currentFilters.endDate;
    }

    // Case Tag Filter
    if (currentFilters.caseTag && currentFilters.caseTag !== 'Any') {
      params.caseTag = currentFilters.caseTag.toLowerCase();
    }

    return params;
  };

  // =====================================================
  // FETCH ACCOUNTS FOR FILTER
  // =====================================================
  useEffect(() => {
    const fetchLabAccounts = async () => {
      try {
        const userLabId = getUserLabId();
        if (!userLabId) return;

        const response = await axios.get(`${API_URL}/api/account/lab-accounts/${userLabId}`);
        if (response.data.success) {
          setAccountsList(response.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching lab accounts:", err);
      }
    };
    fetchLabAccounts();
  }, []);

  // =====================================================
  // FETCH CASES (Dynamically passes filters to backend)
  // =====================================================
  const fetchCases = useCallback(async (page = 1) => {
    const requestId = ++requestIdRef.current;
    try {
      setFilterLoading(true);
      setError(null);
      const userLabId = getUserLabId();
      if (!userLabId) {
        setError("User Lab ID not found. Please login again.");
        return;
      }

      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, {
        params: { ...buildParams(userLabId, filtersRef.current), page, limit: itemsPerPage }
      });

      if (requestId !== requestIdRef.current) return;

      if (response.data.success) {
        const casesData = response.data.data || [];
        const body = response.data;
        const meta = body.pagination || body.meta || {};

        const rawCount = body.totalCount ?? body.total ?? body.totalItems ?? body.totalCases ?? meta.totalCount ?? meta.total ?? meta.totalItems ?? meta.count;
        const parsedCount = Number(rawCount);
        const hasCount = rawCount !== undefined && rawCount !== null && !isNaN(parsedCount);

        const rawPages = body.totalPages ?? body.pages ?? body.pageCount ?? meta.totalPages ?? meta.pages ?? meta.pageCount;
        const parsedPages = Number(rawPages);
        const hasPages = rawPages !== undefined && rawPages !== null && !isNaN(parsedPages) && parsedPages > 0;

        let resolvedPages;
        if (hasPages) resolvedPages = parsedPages;
        else if (hasCount) resolvedPages = Math.max(Math.ceil(parsedCount / itemsPerPage), 1);
        else resolvedPages = 1;

        const minCount = (page - 1) * itemsPerPage + casesData.length;
        const inferredPages = casesData.length >= itemsPerPage ? page + 1 : page;
        let finalPages = Math.max(resolvedPages, inferredPages);

        if (casesData.length === 0 && page > 1) {
          setTotalPages(page - 1);
          setCurrentPage(page - 1);
          return;
        }

        setCases(casesData);
        setTotalPages(finalPages);
        setTotalCount(hasCount ? Math.max(parsedCount, minCount) : minCount);

        const pageSum = casesData.reduce((sum, c) => sum + (c?.totalPrice || c?.totalAmount || 0), 0);
        setTotalAmount(typeof body.totalAmount === 'number' ? body.totalAmount : pageSum);
      } else {
        setError(response.data.message || 'Failed to load cases.');
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setError(error.response?.data?.message || 'Failed to load cases. Please try again.');
    } finally {
      if (requestId === requestIdRef.current) {
        setFilterLoading(false);
        setLoading(false);
      }
    }
  }, [itemsPerPage]);

  useEffect(() => {
    fetchCases(currentPage);
  }, [currentPage, fetchCases]);

  const toggleDropdown = (caseId) => {
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  const calculateTotalAmount = () => totalAmount;

  const formatAmountWithCommas = (amount) => {
    if (!hasPricingAccess) return null;
    const formatted = formatAmount(amount);
    if (typeof formatted !== 'string') return formatted;
    return formatted.replace(/\d+(\.\d+)?/g, (match) => {
      const parts = match.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  };

  const handleAccountClick = (accountName) => {
    const account = { name: accountName };
    if (account) {
      navigate('/tabsWorking', { state: { accountName: accountName, accountData: account } });
    }
  };

  // SHIPPING MODAL
  const handleOpenShippingModal = async () => {
    setShowModal(true);
    setModalLoading(true);
    try {
      const userLabId = getUserLabId();
      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, {
        params: { ...buildParams(userLabId, filtersRef.current), fetchAllFiltered: 'true' }
      });
      if (response.data.success) {
        setModalCases(response.data.data || []);
      } else {
        setModalCases(cases);
      }
    } catch (e) {
      setModalCases(cases);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseShippingModal = () => setShowModal(false);
  const handleCloseForShipping = () => setShowModal(false);

  // =====================================================
  // FILTER MODAL HANDLERS
  // =====================================================
  const openFilterModal = () => {
    setTempFilters({ ...filters });
    setShowFilterModal(true);
  };

  const closeFilterModal = () => setShowFilterModal(false);

  const handleFilterChange = (field, value) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    filtersRef.current = { ...tempFilters };
    setFilters({ ...tempFilters });
    setShowFilterModal(false);

    if (currentPage === 1) {
      fetchCases(1);
    } else {
      setCurrentPage(1);
    }
  };

  // RESET FILTERS FUNCTION
  const resetFilters = () => {
    filtersRef.current = defaultFilters;
    setTempFilters(defaultFilters);
    setFilters(defaultFilters);
    setShowFilterModal(false);

    if (currentPage === 1) {
      fetchCases(1);
    } else {
      setCurrentPage(1);
    }
  };

  // Client-side sorting only (Backend filters the data)
  const filterSortedCases = [...cases].sort((a, b) => {
    let valA, valB;
    if (filters.sortBy === 'Files Count') {
      valA = a.procedures?.length || 0;
      valB = b.procedures?.length || 0;
    } else if (filters.sortBy === 'Delivery Date') {
      valA = new Date(a.delivery_date).getTime() || 0;
      valB = new Date(b.delivery_date).getTime() || 0;
    } else {
      valA = a.delivery_date;
      valB = b.delivery_date;
    }

    if (filters.sortDirection === 'Descending') {
      return valA < valB ? 1 : -1;
    } else {
      return valA > valB ? 1 : -1;
    }
  });

  // Header-arrow sort (if clicked) overrides the filter-modal sort
  const filteredCases = sortConfig.key ? sortByColumn(cases, sortConfig) : filterSortedCases;

  // =====================================================
  // CSV EXPORT FUNCTION
  // =====================================================
  const handleCSVExport = () => {
    if (!filteredCases || filteredCases.length === 0) {
      alert("No cases available to export.");
      return;
    }

    const headers = [
      "Case Number", "Pan Color", "Procedures", "Invoice Number",
      "Account", "Provider", "Patient", "Account Left (Start Date)",
      "Lab Ship (Arrive Date)", "Delivery Date"
    ];

    if (hasPricingAccess) {
      headers.push("Total Price");
    }

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const rows = filteredCases.map((c) => {
      const proceduresStr = c.procedures && c.procedures.length > 0
        ? c.procedures.map(p => p.procedure).join(", ")
        : "No procedures";

      const row = [
        c.caseNumber || "N/A",
        c.color || "N/A",
        proceduresStr,
        c.invoiceNumber || "N/A",
        c.account_name || c.account_holder || "N/A",
        c.provider_name || "N/A",
        c.last_name || "N/A",
        c.start_date ? new Date(c.start_date).toLocaleDateString() : "N/A",
        c.arrive_date ? new Date(c.arrive_date).toLocaleDateString() : "N/A",
        c.delivery_date ? new Date(c.delivery_date).toLocaleDateString() : "N/A"
      ];

      if (hasPricingAccess) {
        const price = c.totalPrice || c.totalAmount || 0;
        row.push(formatAmountWithCommas(price) || price);
      }

      return row.map(escapeCSV).join(",");
    });

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Cases_To_Ship_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activeFilterCount =
    (filters.by && filters.by !== 'To Ship' ? 1 : 0) +
    (filters.account ? 1 : 0) +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0);

  // PAGINATION
  const handleFirstPage = () => setCurrentPage(1);
  const handlePreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    }
    return pageNumbers;
  };

  if (loading || pricingLoading) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="cases-loading"><p>Loading cases...</p></div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="cases-error"><p style={{ color: 'red' }}>{error}</p></div>
      </>
    );
  }

  const shipCount = Math.max(totalCount, cases.length);

  return (
    <>
      <Header />
      <Navvbar />
      <div className="cases-page cases-ship-page" style={{ position: 'relative' }}>

        {/* Header with Tabs/Buttons */}
        <div className="cases-header">
          <h2 className="cases-title">
            {hasPricingAccess ? `Cases To Ship (${formatAmountWithCommas(calculateTotalAmount())})` : `Cases To Ship`}
          </h2>
          <div className="action-buttons">
            <Button
              className="tab-btn close-ship-btn"
              style={{
                backgroundColor: shipCount > 0 ? '#004aad' : '#ccc',
                borderColor: shipCount > 0 ? '#004aad' : '#ccc',
                color: shipCount > 0 ? 'white' : '#666'
              }}
              disabled={shipCount === 0}
              onClick={handleOpenShippingModal}
            >
              <FaTruck /> Close for Shipping ({shipCount})
            </Button>
            <Button className="tab-btn scan-btn">
              <Link to="/ShipScan" style={{ textDecoration: 'none', color: 'inherit' }}>
                <PiScanFill /> Scan
              </Link>
            </Button>

            <Button className="tab-btn filter-btn" onClick={openFilterModal}>
              <LuFilter /> Filters
            </Button>

            <Button className="tab-btn csv-btn" onClick={handleCSVExport}>
              <FiFileText /> CSV
            </Button>
          </div>
        </div>

        {/* Loading overlay */}
        {filterLoading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.85)', display: 'flex',
            flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            zIndex: 100, minHeight: '300px'
          }}>
            <div style={{
              width: '40px', height: '40px', border: '4px solid #f3f3f3',
              borderTop: '4px solid #004aad', borderRadius: '50%',
              animation: 'caseShipSpin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '10px' }}>Loading cases...</p>
          </div>
        )}

        {/* Table Wrapper */}
        <div className="cases-table-wrapper">
          <table className="cases-table">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '4%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>

            <thead>
              <tr>
                <SortHeader
                  label={
                    <span className="icon-tooltip" data-tooltip="Case Status">
                      <FaBox className="table-icon header-box-icon icon-inbox" />
                    </span>
                  }
                  colKey="status"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <th className="table-header">
                  <GrAttachment className="table-icon icon-attachment" />
                </th>
                <SortHeader label="Case/Pan" colKey="caseNumber" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Invoice" colKey="invoice" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account" colKey="account" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Provider" colKey="provider" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Patient/Appt" colKey="patient" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account Left" colKey="accountShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Lab Ship" colKey="labShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Delivery Date" colKey="delivery" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>

            <tbody>
              {filteredCases.length > 0 ? (
                filteredCases.map((caseItem) => {
                  const statusInfo = getStatusIcon(caseItem);
                  const StatusIcon = statusInfo.Icon;

                  return (
                    <tr
                      key={caseItem._id}
                      className="case-row"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e0e0e0';
                        const cells = e.currentTarget.getElementsByTagName('td');
                        for (let i = 0; i < cells.length; i++) cells[i].style.backgroundColor = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        const cells = e.currentTarget.getElementsByTagName('td');
                        for (let i = 0; i < cells.length; i++) cells[i].style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Status icon: same as Cases Opened */}
                      <td className="table-cell status-cell">
                        <span className="icon-tooltip" data-tooltip={statusInfo.tip}>
                          <StatusIcon className={statusInfo.className} />
                        </span>
                      </td>
                      <td className="table-cell">
                        <GrAttachment className="table-icon icon-attachment" />
                      </td>
                      <td className="case-cell">
                        <div className="case-top-row">

                          <div className="case-info-col">
                            <div className="case-number-row">
                              <Link to={`/case/${caseItem.caseNumber}`} className="case-link">
                                #{caseItem.caseNumber}
                              </Link>
                              <button className="settings-button" onClick={(e) => { e.stopPropagation(); toggleDropdown(caseItem._id); }}>
                                <IoSettingsOutline className="settings-icon" />
                              </button>
                            </div>
                            <div className="pan-color">
                              <span>Pan Color:</span>
                              <span className="pan-color-box" style={{ backgroundColor: caseItem.color ? caseItem.color.toLowerCase() : "#ccc" }}></span>
                              <span>{caseItem.color || "N/A"}</span>
                            </div>
                            {caseItem.procedures && caseItem.procedures.length > 0 ? (
                              <div className="procedures">
                                {caseItem.procedures.slice(0, 2).map((proc, index) => <div key={index}>{proc.procedure}</div>)}
                                {caseItem.procedures.length > 2 && <div>+{caseItem.procedures.length - 2} more</div>}
                              </div>
                            ) : (
                              <div className="procedures">No procedures selected</div>
                            )}
                          </div>

                        </div>
                        {dropdownVisible === caseItem._id && (
                          <div className="case-dropdown">
                            <div className="dropdown-item"><BsFileEarmarkCode /> Close/Invoice</div>
                            <div className="dropdown-item"><BsPencilFill /> Add Notes</div>
                            <div className="dropdown-item dropdown-item-last"><AiOutlineFileAdd /> Upload Files</div>
                          </div>
                        )}
                      </td>
                      <td className="invoice-cell">{caseItem.invoiceNumber || "N/A"}</td>
                      <td className="table-cell account-cell">
                        <span onClick={() => handleAccountClick(caseItem.account_name || caseItem.account_holder)} className="account-link">
                          {caseItem.account_name || caseItem.account_holder || "N/A"}
                        </span>
                      </td>
                      <td className="table-cell">{caseItem.provider_name || "N/A"}</td>
                      <td className="table-cell">{caseItem.last_name || "N/A"}</td>
                      <td className="table-cell">{formatShortDate(caseItem.start_date)}</td>
                      <td className="table-cell">{formatShortDate(caseItem.arrive_date)}</td>
                      <td className="table-cell">{formatShortDate(caseItem.delivery_date)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="no-cases">
                    {activeFilterCount > 0 ? "No cases match the applied filters." : "No cases to ship this month."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <Button className="pagination-button" onClick={handleFirstPage} disabled={currentPage === 1}>First</Button>
          <Button className="pagination-button" onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</Button>
          {getPageNumbers().map(number => (
            <Button key={number} className={`pagination-button ${currentPage === number ? 'active' : 'outline'}`} onClick={() => setCurrentPage(number)}>{number}</Button>
          ))}
          <Button className="pagination-button outline" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</Button>
          <Button className="pagination-button outline" onClick={handleLastPage} disabled={currentPage === totalPages}>Last</Button>
        </div>
      </div>

      {/* ================= FILTER MODAL ================= */}
      <Modal show={showFilterModal} onHide={closeFilterModal} centered size="md" className="custom-filter-modal">
        <Modal.Header closeButton style={{ backgroundColor: '#004aad', color: 'white', padding: '15px 20px' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 'normal' }}>Change Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 25px', backgroundColor: '#fff' }}>

          <h6 style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '15px' }}>Filters</h6>

          <InputGroup className="mb-2 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>By</InputGroup.Text>
            <Form.Select value={tempFilters.by} onChange={(e) => handleFilterChange('by', e.target.value)}>
              <option value="Month">Month</option>
              <option value="To Arrive">To Arrive</option>
              <option value="To Ship">To Ship</option>
              <option value="Create Shipments">Create Shipments</option>
              <option value="Open Try-Ins">Open Try-Ins</option>
              <option value="On Hold">On Hold</option>
              <option value="To Outsource">To Outsource</option>
              <option value="Case plans / Estimates">Case plans / Estimates</option>
              <option value="Open Cases">Open Cases</option>
              <option value="Undeliverable Cases">Undeliverable Cases</option>
            </Form.Select>
          </InputGroup>

          <InputGroup className="mb-2 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>Account</InputGroup.Text>
            <Form.Select
              value={tempFilters.account}
              onChange={(e) => handleFilterChange('account', e.target.value)}
            >
              <option value="">All Accounts</option>
              {accountsList.map((acc) => (
                <option key={acc._id || acc.accountId} value={acc.account_name}>
                  {acc.account_name}
                </option>
              ))}
            </Form.Select>
          </InputGroup>

          <InputGroup className="mb-2 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>Case Tag</InputGroup.Text>
            <Form.Select value={tempFilters.caseTag} onChange={(e) => handleFilterChange('caseTag', e.target.value)}>
              <option value="Any">Any</option>
              <option value="Urgent">Urgent</option>
              <option value="Normal">Normal</option>
            </Form.Select>
          </InputGroup>

          <InputGroup className="mb-4 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>Properties</InputGroup.Text>
            <Form.Select value={tempFilters.properties} onChange={(e) => handleFilterChange('properties', e.target.value)}>
              <option value="All">All</option>
              <option value="Specific">Specific</option>
            </Form.Select>
          </InputGroup>

          <h6 style={{ fontWeight: 'bold', fontSize: '15px', marginTop: '20px', marginBottom: '15px' }}>Look Up Date Range</h6>

          <InputGroup className="mb-2 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>Start Date</InputGroup.Text>
            <Form.Control type="date" placeholder="dd/mm/yyyy" value={tempFilters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
          </InputGroup>

          <InputGroup className="mb-4 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>End Date</InputGroup.Text>
            <Form.Control type="date" placeholder="dd/mm/yyyy" value={tempFilters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
          </InputGroup>

          <h6 style={{ fontWeight: 'bold', fontSize: '15px', marginTop: '20px', marginBottom: '15px' }}>Shipping Options</h6>

          <InputGroup className="mb-4 custom-input-group">
            <InputGroup.Text style={{ width: '160px' }}>Closed Cases, Not Shipped</InputGroup.Text>
            <Form.Select value={tempFilters.closedCases} onChange={(e) => handleFilterChange('closedCases', e.target.value)}>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Form.Select>
          </InputGroup>

          <h6 style={{ fontWeight: 'bold', fontSize: '15px', marginTop: '20px', marginBottom: '15px' }}>Sorting</h6>

          <InputGroup className="mb-2 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>Sort By</InputGroup.Text>
            <Form.Select value={tempFilters.sortBy} onChange={(e) => handleFilterChange('sortBy', e.target.value)}>
              <option value="Files Count">Files Count</option>
              <option value="Delivery Date">Delivery Date</option>
            </Form.Select>
          </InputGroup>

          <InputGroup className="mb-2 custom-input-group">
            <InputGroup.Text style={{ width: '110px' }}>Sort Direction</InputGroup.Text>
            <Form.Select value={tempFilters.sortDirection} onChange={(e) => handleFilterChange('sortDirection', e.target.value)}>
              <option value="Descending">Descending</option>
              <option value="Ascending">Ascending</option>
            </Form.Select>
          </InputGroup>

        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '15px 20px', backgroundColor: '#fff' }}>
          <Button variant="light" onClick={resetFilters} style={{ backgroundColor: '#f0f0f0', border: '1px solid #ddd', color: '#333', fontWeight: '500' }}>
            Reset
          </Button>
          <Button onClick={applyFilters} style={{ backgroundColor: '#004aad', borderColor: '#004aad', fontWeight: '500' }}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Shipping Confirmation Modal */}
      <Modal show={showModal} onHide={handleCloseShippingModal}>
        <Modal.Header closeButton style={{ backgroundColor: '#004aad', color: 'white' }}>
          <Modal.Title>Confirm Cases to Ship</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are about to ship the following cases:</p>
          {modalLoading ? (
            <p>Loading cases...</p>
          ) : modalCases.length > 0 ? (
            <>
              <ul>
                {modalCases.map((caseItem) => (
                  <li key={caseItem._id}>
                    Case #{caseItem?.caseNumber || 'N/A'} - {caseItem?.account_name || caseItem?.account_holder || 'N/A'}
                  </li>
                ))}
              </ul>
              {hasPricingAccess && (
                <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
                  Total Amount: {formatAmountWithCommas(modalCases.reduce((s, c) => s + (c?.totalPrice || c?.totalAmount || 0), 0))}
                </p>
              )}
            </>
          ) : (
            <p>No cases available to ship.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseShippingModal}>Cancel</Button>
          <Button style={{ backgroundColor: '#004aad', borderColor: '#004aad' }} onClick={handleCloseForShipping} disabled={modalLoading || modalCases.length === 0}>
            Confirm Shipment
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CaseShip;